import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactFlow, {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  Panel,
} from "reactflow";
import "reactflow/dist/style.css";

import StartNode from "./nodes/StartNode";
import QuestionNode from "./nodes/QuestionNode";
import TerminalNode from "./nodes/TerminalNode";
import Sidebar from "./Sidebar";
import NodeConfigPanel from "./NodeConfigPanel";
import ValidationPanel from "./ValidationPanel";
import {
  NodeTypes as NodeTypeKeys,
  QuestionTypes,
  DEFAULT_LIST_OPTIONS,
} from "../utils/constants";
import { generateNodeId } from "../utils/flowSerializer";
import { isValidNewConnection } from "../utils/flowValidator";
import {
  Handle,
  edgeStrokeForHandle,
  isListHandle,
  listHandleIndex,
} from "../utils/flowSchema";
import { DiagnosticFlowResourcesProvider } from "../context/diagnosticFlowResources";
import appFetch from "../../../../../api/appFetch";
import { OutletUserRolesUrl } from "../../../../../constants/apiUrls";

const nodeTypes = {
  [NodeTypeKeys.START]: StartNode,
  [NodeTypeKeys.QUESTION]: QuestionNode,
  [NodeTypeKeys.TERMINAL]: TerminalNode,
};

const defaultEdgeOptions = {
  type: "smoothstep",
  style: { strokeWidth: 2 },
  animated: false,
};

const initialNodes = [
  {
    id: "start_1",
    type: NodeTypeKeys.START,
    position: { x: 300, y: 30 },
    data: { label: "" },
    deletable: false,
  },
];

export default function FlowCanvas({
  nodes: externalNodes,
  edges: externalEdges,
  onChange,
  validation,
  onValidationClose,
  resources = {},
  onOpenTranslations,
  translationPercents = {},
  onInstanceReady,
}) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes] = useNodesState(externalNodes || initialNodes);
  const [edges, setEdges] = useEdgesState(externalEdges || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [roles, setRoles] = useState([]);

  // Fetch the outlet-user roles once per session; node clicks reuse this list.
  useEffect(() => {
    appFetch({
      url: OutletUserRolesUrl(),
      onSuccess: (response) => setRoles(response || []),
      onError: () => {},
    });
  }, []);

  useEffect(() => {
    if (!validation || !validation.invalidNodeIds) return;

    const invalidSet = new Set(validation.invalidNodeIds);
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, _invalid: invalidSet.has(n.id) },
      }))
    );
  }, [validation, setNodes]);

  const notifyChange = useCallback(
    (newNodes, newEdges) => {
      if (onChange) onChange(newNodes || nodes, newEdges || edges);
    },
    [onChange, nodes, edges]
  );

  // the label and colour follow the source handle, so a relink must rebuild them
  const buildEdgeProps = useCallback(
    (params) => {
      const strokeColor = edgeStrokeForHandle(params.sourceHandle);

      // List branches stay unlabelled; the option already shows on the node.
      let label;
      if (params.sourceHandle === Handle.YES) {
        label = resources["Yes"];
      } else if (params.sourceHandle === Handle.NO) {
        label = resources["No"];
      } else if (!isListHandle(params.sourceHandle)) {
        label = resources["Continue"];
      }

      return {
        ...params,
        type: "smoothstep",
        label,
        labelStyle: { fontWeight: 600, fontSize: "12px" },
        labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
        style: { stroke: strokeColor, strokeWidth: 2 },
      };
    },
    [resources]
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const updated = addEdge(buildEdgeProps(params), eds);
        notifyChange(nodes, updated);
        return updated;
      });
    },
    [setEdges, notifyChange, nodes, buildEdgeProps]
  );

  // a missed drop keeps the edge — relink or nothing, never delete-on-miss
  const updatingEdgeIdRef = useRef(null);

  const onReconnectStart = useCallback((_, edge) => {
    updatingEdgeIdRef.current = edge.id;
  }, []);

  const onReconnectEnd = useCallback(() => {
    updatingEdgeIdRef.current = null;
  }, []);

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      setEdges((eds) => {
        const updated = eds.map((e) =>
          e.id === oldEdge.id ? { ...e, ...buildEdgeProps(newConnection) } : e
        );
        notifyChange(nodes, updated);
        return updated;
      });
    },
    [setEdges, notifyChange, nodes, buildEdgeProps]
  );

  const handleNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => {
        const updated = applyNodeChanges(changes, nds);
        const structural = changes.some(
          (c) =>
            c.type === "remove" ||
            c.type === "add" ||
            c.type === "reset" ||
            (c.type === "position" && c.dragging !== true)
        );
        if (structural) notifyChange(updated, edges);
        return updated;
      });
    },
    [setNodes, notifyChange, edges]
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => {
        const updated = applyEdgeChanges(changes, eds).map((e) => ({
          ...e,
          style: { ...e.style, strokeWidth: e.selected ? 4 : 2 },
        }));
        const structural = changes.some(
          (c) => c.type === "remove" || c.type === "add" || c.type === "reset"
        );
        if (structural) notifyChange(nodes, updated);
        return updated;
      });
    },
    [setEdges, notifyChange, nodes]
  );

  const handleIsValidConnection = useCallback(
    (connection) => {
      return isValidNewConnection(connection, nodes, edges, updatingEdgeIdRef.current);
    },
    [nodes, edges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData("application/reactflow");
      if (!raw || !reactFlowInstance) return;

      const template = JSON.parse(raw);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - (template.grabOffsetX || 0),
        y: event.clientY - (template.grabOffsetY || 0),
      });

      const data = { label: "" };
      if (template.type === NodeTypeKeys.QUESTION) {
        data.questionType = template.questionType || QuestionTypes.FREE_TEXT;
        if (data.questionType === QuestionTypes.LIST) {
          data.listOptions = [...DEFAULT_LIST_OPTIONS];
        }
      }

      const newNode = {
        id: generateNodeId(),
        type: template.type,
        position,
        data,
      };

      setNodes((nds) => {
        const updated = nds.concat(newNode);
        notifyChange(updated, edges);
        return updated;
      });
      setSelectedNode(newNode);
    },
    [reactFlowInstance, setNodes, notifyChange, edges]
  );

  const handleNodeDelete = useCallback(
    (nodeId) => {
      setNodes((nds) => {
        const target = nds.find((n) => n.id === nodeId);
        if (target && target.type === NodeTypeKeys.START) return nds;

        const updated = nds.filter((n) => n.id !== nodeId);
        setEdges((eds) => {
          const updatedEdges = eds.filter(
            (e) => e.source !== nodeId && e.target !== nodeId
          );
          notifyChange(updated, updatedEdges);
          return updatedEdges;
        });
        notifyChange(updated, edges);
        return updated;
      });
      setSelectedNode(null);
    },
    [setNodes, setEdges, notifyChange, edges]
  );

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodeUpdate = useCallback(
    (nodeId, newData) => {
      setNodes((nds) => {
        const updated = nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...newData } } : n
        );
        const updatedNode = updated.find((n) => n.id === nodeId);
        setSelectedNode(updatedNode || null);

        // Drop edges whose list option was removed (handles are index-based).
        setEdges((eds) => {
          const options = newData.listOptions || [];
          const reconciled = eds.filter(
            (e) =>
              e.source !== nodeId ||
              !isListHandle(e.sourceHandle) ||
              listHandleIndex(e.sourceHandle) < options.length
          );
          notifyChange(updated, reconciled);
          return reconciled;
        });

        return updated;
      });
    },
    [setNodes, setEdges, notifyChange]
  );

  const handleInit = useCallback(
    (instance) => {
      setReactFlowInstance(instance);
      if (onInstanceReady) onInstanceReady(instance);

      const hasExisting =
        Array.isArray(externalNodes) && externalNodes.length > 0;
      if (hasExisting) {
        instance.fitView({ padding: 0.4, maxZoom: 0.85 });
        return;
      }

      const zoom = 1;
      const wrapperWidth = reactFlowWrapper.current?.clientWidth || 1000;
      const start = initialNodes[0];
      instance.setViewport({
        x: wrapperWidth / 2 - (start.position.x + 90) * zoom,
        y: 40,
        zoom,
      });
    },
    [externalNodes, onInstanceReady]
  );

  const focusNode = useCallback(
    (nodeId) => {
      if (!reactFlowInstance) return;
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      reactFlowInstance.setCenter(
        node.position.x + 100,
        node.position.y + 50,
        { zoom: 1.2, duration: 600 }
      );
      setSelectedNode(node);
    },
    [reactFlowInstance, nodes]
  );

  const nodeCount = nodes.length;
  const edgeCount = edges.length;

  return (
    <DiagnosticFlowResourcesProvider value={resources}>
    <div style={{ display: "flex", height: "100%", width: "100%", position: "relative" }}>
      <Sidebar resources={resources} />

      <div
        ref={reactFlowWrapper}
        style={{ flex: 1, height: "100%", position: "relative" }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onReconnect={onReconnect}
          onReconnectStart={onReconnectStart}
          onReconnectEnd={onReconnectEnd}
          isValidConnection={handleIsValidConnection}
          onInit={handleInit}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          snapToGrid
          snapGrid={[15, 15]}
          deleteKeyCode={["Backspace", "Delete"]}
          connectionLineStyle={{ stroke: "#1a73e8", strokeWidth: 2 }}
          connectionLineType="smoothstep"
        >
          <Controls
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          />
          <MiniMap
            nodeStrokeWidth={3}
            zoomable
            pannable
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            nodeColor={(n) => {
              if (n.type === NodeTypeKeys.START) return "#43a047";
              if (n.type === NodeTypeKeys.TERMINAL) return "#e53935";
              return "#1976d2";
            }}
          />
          <Background variant="dots" gap={20} size={1} color="#ddd" />
          <Panel position="top-right" style={{ margin: "8px" }}>
            <div
              style={{
                background: "#fff",
                padding: "8px 14px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: "12px",
                color: "#666",
                fontWeight: 500,
                display: "flex",
                gap: "12px",
              }}
            >
              <span>
                <strong style={{ color: "#333" }}>{nodeCount}</strong>{" "}
                {resources["DiagnosticFlowNodesLabel"]}
              </span>
              <span style={{ color: "#ddd" }}>|</span>
              <span>
                <strong style={{ color: "#333" }}>{edgeCount}</strong>{" "}
                {resources["DiagnosticFlowConnectionsLabel"]}
              </span>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      <NodeConfigPanel
        selectedNode={selectedNode}
        onNodeUpdate={handleNodeUpdate}
        onNodeDelete={handleNodeDelete}
        onClose={() => setSelectedNode(null)}
        onOpenTranslations={onOpenTranslations}
        translationPercent={selectedNode ? translationPercents[selectedNode.id] || 0 : 0}
        resources={resources}
        roles={roles}
      />

      <ValidationPanel
        validation={validation}
        onClose={onValidationClose}
        onNodeClick={focusNode}
        resources={resources}
      />
    </div>
    </DiagnosticFlowResourcesProvider>
  );
}
